import { combineReducers } from 'redux';

import error from 'modules/global/reducers/generic.error';
import ids, { clearAll } from 'modules/global/reducers/generic.ids';
import loadingReducer from 'modules/global/reducers/generic.loading';

import {
  DELETE_INTERVENTION_REQUEST,
  FETCH_INTERVENTIONS_PDF,
  FETCH_LINKED_INTERVENTIONS_REQUEST,
  PUSH_BACK_DMS_REQUEST,
  UPDATE_INTERVENTION_STATUS_REQUEST,
  FETCH_INTERVENTION_REQUEST,
  SEND_INTERVENTION_REQUEST,
} from 'modules/interventions/actionTypes';
import { TRANSLATIONS_PAGE_READY } from 'modules/predefinedMessages/actionTypes';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { createReducer } from 'utils/ReduxUtils';

import inspections from './inspections';
import isUpdating from './isUpdating';
import kioskPreference from './kioskPreference';
import share from './share';
import step from './step';
import tags from './tags';
import worksImportModalVisible from './worksImportModalVisible';

/**
 * @typedef {{
 *   share: import("./share.js").InterventionUIDetailsShareReducer,
 *   isUpdating: boolean,
 *   kioskPreference: import('./kioskPreference.js').KioskPreferenceState,
 *   inspections: {
 *     data: {
 *       ids: string[],
 *       entities: Record<string, import('../../../types/inspections').Inspection>,
 *     },
 *     loading: {
 *       global: boolean,
 *       importing: string[],
 *       customerView: string[],
 *       exportMedia: string[],
 *       patch: string[],
 *       remove: string[],
 *     },
 *   },
 *   printing: boolean,
 *   sending: boolean,
 *   integrating: boolean,
 *   isFetching: boolean,
 *   apiError: string,
 *   others: {
 *     ids: string[],
 *     loading: boolean,
 *   },
 *   worksImportModalVisible: boolean,
 *   ready: boolean,
 *   status: {
 *     isDeleting: boolean,
 *     isPatchingStatus: boolean,
 *   },
 *   step: import("./step.js").InterventionUIDetailsStepReducer,
 *   tags: import("@arhs-fleetback/types/entities/tags").BasicTag[],
 * }} InterventionsUIDetailsReducer
 */

export default combineReducers({
  share,
  isUpdating,
  kioskPreference,
  inspections,
  printing: loadingReducer(FETCH_INTERVENTIONS_PDF),
  sending: loadingReducer(SEND_INTERVENTION_REQUEST),
  integrating: loadingReducer(PUSH_BACK_DMS_REQUEST),
  isFetching: loadingReducer(FETCH_INTERVENTION_REQUEST, {
    global: {
      matcher: action => !action.meta?.silent,
    },
  }),
  apiError: error(FETCH_INTERVENTION_REQUEST, {
    global: {
      matcher: action => !action.meta?.silent,
    },
    reducers: {
      [LOCATION_CHANGE]: () => null,
    }
  }),
  others: combineReducers({
    ids: ids(FETCH_LINKED_INTERVENTIONS_REQUEST, {
      reducers: {
        [LOCATION_CHANGE]: clearAll
      },
    }),
    loading: loadingReducer(FETCH_LINKED_INTERVENTIONS_REQUEST)
  }),
  worksImportModalVisible,
  ready: createReducer(false, {
    [LOCATION_CHANGE]: () => false,
    [TRANSLATIONS_PAGE_READY]: () => true,
  }),
  status: combineReducers({
    isDeleting: loadingReducer(DELETE_INTERVENTION_REQUEST),
    isPatchingStatus: loadingReducer(UPDATE_INTERVENTION_STATUS_REQUEST),
  }),
  step,
  tags,
});
